import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, ListChecks } from 'lucide-react';
import {
  atlasApi, AtlasUser, OnboardingTrilha, OnboardingEtapaTrilha,
  OnboardingEtapaTipo, OnboardingResponsavelTipo,
} from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import ListaEditavel, { inputCls, labelCls } from '../shared/ListaEditavel';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

const TIPO_LABEL: Record<OnboardingEtapaTipo, string> = {
  tarefa: 'Tarefa', documento: 'Documento', treinamento: 'Treinamento', reuniao: 'Reunião',
};
const RESP_LABEL: Record<OnboardingResponsavelTipo, string> = {
  gestor: 'Gestor', rh: 'RH', ti: 'TI', colaborador: 'Colaborador',
};

function novaEtapa(): OnboardingEtapaTrilha {
  return {
    id: `e${Date.now()}${Math.floor(Math.random() * 1000)}`,
    titulo: '', tipo: 'tarefa', responsavel_tipo: 'rh', dias_apos_admissao: 0, descricao: '',
  };
}

// CSV <-> array helpers (departamentos / cargos como linha separada por vírgula)
const toCsv = (arr: string[]) => (arr || []).join(', ');
const fromCsv = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);

interface FormState {
  nome: string;
  descricao: string;
  ativo: boolean;
  departamentos: string;
  cargos: string;
  etapas: OnboardingEtapaTrilha[];
}

const vazio = (): FormState => ({ nome: '', descricao: '', ativo: true, departamentos: '', cargos: '', etapas: [novaEtapa()] });

const Trilhas: React.FC<Props> = ({ user, podeEditar }) => {
  const [itens, setItens] = useState<OnboardingTrilha[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(vazio());
  const [salvando, setSalvando] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getOnboardingTrilhas(user)
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirNova = () => {
    setEditId(null);
    setForm(vazio());
    setFb(null);
    setModalAberto(true);
  };

  const abrirEdicao = (t: OnboardingTrilha) => {
    setEditId(t._id);
    setForm({
      nome: t.nome || '',
      descricao: t.descricao || '',
      ativo: t.ativo !== false,
      departamentos: toCsv(t.aplicavel_a?.departamentos || []),
      cargos: toCsv(t.aplicavel_a?.cargos || []),
      etapas: (t.etapas && t.etapas.length ? t.etapas : [novaEtapa()]).map(e => ({ ...e })),
    });
    setFb(null);
    setModalAberto(true);
  };

  const salvar = async () => {
    if (!form.nome.trim()) { setFb({ tipo: 'erro', msg: 'Nome é obrigatório.' }); return; }
    const etapas = form.etapas
      .filter(e => e.titulo.trim())
      .map(e => ({
        ...e,
        dias_apos_admissao: Number(e.dias_apos_admissao) || 0,
        descricao: e.descricao?.trim() || undefined,
      }));
    if (etapas.length === 0) { setFb({ tipo: 'erro', msg: 'Adicione ao menos uma etapa com título.' }); return; }
    setSalvando(true);
    setFb(null);
    const body = {
      nome: form.nome.trim(),
      descricao: form.descricao.trim() || undefined,
      ativo: form.ativo,
      aplicavel_a: { departamentos: fromCsv(form.departamentos), cargos: fromCsv(form.cargos) },
      etapas,
    };
    try {
      if (editId) await atlasApi.atualizarOnboardingTrilha(user, editId, body);
      else await atlasApi.criarOnboardingTrilha(user, body);
      setModalAberto(false);
      carregar();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (t: OnboardingTrilha) => {
    if (!window.confirm(`Excluir a trilha "${t.nome}"?`)) return;
    try {
      await atlasApi.excluirOnboardingTrilha(user, t._id);
      carregar();
    } catch (e: any) {
      setErro(e.message || 'Erro ao excluir.');
    }
  };

  const aplicabilidade = (t: OnboardingTrilha): string => {
    const deps = t.aplicavel_a?.departamentos || [];
    const cargos = t.aplicavel_a?.cargos || [];
    const partes: string[] = [];
    if (deps.length) partes.push(`${deps.length} depto${deps.length > 1 ? 's' : ''}`);
    if (cargos.length) partes.push(`${cargos.length} cargo${cargos.length > 1 ? 's' : ''}`);
    return partes.length ? partes.join(' · ') : 'Todos';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex-1">Trilhas de integração</h2>
        {podeEditar && (
          <button onClick={abrirNova} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nova trilha
          </button>
        )}
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : itens.length === 0 ? (
        <div className="p-12 text-center text-gray-400 bg-white rounded-2xl border border-gray-200">Nenhuma trilha cadastrada.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {itens.map(t => (
            <div key={t._id} className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-col gap-2">
              <div className="flex items-start gap-2">
                <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0"><ListChecks className="w-4 h-4" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-900">{t.nome}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${t.ativo ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {t.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  {t.descricao && <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{t.descricao}</p>}
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                <span>{(t.etapas || []).length} etapa{(t.etapas || []).length !== 1 ? 's' : ''}</span>
                <span>Aplicável a: {aplicabilidade(t)}</span>
              </div>
              {podeEditar && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                  <button onClick={() => abrirEdicao(t)} className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button onClick={() => excluir(t)} className="text-xs text-rose-600 hover:underline flex items-center gap-1 ml-auto">
                    <Trash2 className="w-3.5 h-3.5" /> Excluir
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalAberto && (
        <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModalAberto(false)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900 flex-1">{editId ? 'Editar trilha' : 'Nova trilha'}</h3>
              <button onClick={() => setModalAberto(false)} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className={labelCls}>Nome *</label>
                <input className={inputCls} value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Onboarding comercial" />
              </div>
              <div>
                <label className={labelCls}>Descrição</label>
                <textarea className={`${inputCls} min-h-[60px] resize-y`} value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Departamentos (separados por vírgula)</label>
                  <input className={inputCls} value={form.departamentos} onChange={e => setForm(p => ({ ...p, departamentos: e.target.value }))} placeholder="01, 02" />
                </div>
                <div>
                  <label className={labelCls}>Cargos (separados por vírgula)</label>
                  <input className={inputCls} value={form.cargos} onChange={e => setForm(p => ({ ...p, cargos: e.target.value }))} placeholder="Vendedor, Analista" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={form.ativo} onChange={e => setForm(p => ({ ...p, ativo: e.target.checked }))} />
                Trilha ativa
              </label>

              <div className="pt-2">
                <label className={labelCls}>Etapas</label>
                <ListaEditavel<OnboardingEtapaTrilha>
                  itens={form.etapas}
                  onChange={etapas => setForm(p => ({ ...p, etapas }))}
                  novoItem={novaEtapa}
                  rotuloAdd="adicionar etapa"
                  vazioMsg="Nenhuma etapa."
                  renderLinha={(et, atualizar) => (
                    <div className="space-y-2">
                      <input className={inputCls} placeholder="Título da etapa" value={et.titulo} onChange={e => atualizar({ titulo: e.target.value })} />
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <select className={inputCls} value={et.tipo} onChange={e => atualizar({ tipo: e.target.value as OnboardingEtapaTipo })}>
                          {(Object.keys(TIPO_LABEL) as OnboardingEtapaTipo[]).map(k => <option key={k} value={k}>{TIPO_LABEL[k]}</option>)}
                        </select>
                        <select className={inputCls} value={et.responsavel_tipo} onChange={e => atualizar({ responsavel_tipo: e.target.value as OnboardingResponsavelTipo })}>
                          {(Object.keys(RESP_LABEL) as OnboardingResponsavelTipo[]).map(k => <option key={k} value={k}>{RESP_LABEL[k]}</option>)}
                        </select>
                        <input className={inputCls} type="number" min={0} placeholder="Dias após admissão" value={et.dias_apos_admissao} onChange={e => atualizar({ dias_apos_admissao: Number(e.target.value) })} />
                      </div>
                      <input className={inputCls} placeholder="Descrição (opcional)" value={et.descricao || ''} onChange={e => atualizar({ descricao: e.target.value })} />
                    </div>
                  )}
                />
              </div>

              <FeedbackBanner feedback={fb} />

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setModalAberto(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                <button onClick={salvar} disabled={salvando} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
                  {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trilhas;
